/* =====================================================================
   WM2 — WEBMCP: THE LADDER

   WM1 (2026-09-21) put two doors on `document.modelContext` — the
   terminal and the state — because that is what the relay already had and
   the point was to prove the same two doors work from inside the tab.

   WM2 is Tommy's design, 2026-09-22, in his words: "we offer it from the
   start, but we restrict the tools available, so essentially you can
   unlock tools as you go". And the reason: "hand the whole site from the
   start over to an agent as a form of eval/benchmark ... to see how
   quickly it can work its way through the site". And the shape of the
   first tool: "a human can see the whole of the map in view at once, so
   you might want something like that."

   SO THERE IS A LADDER. Four tools stand on the page from the first
   frame — LOOK, READ, MOVE, ACT — and they are the eyes and the hands a
   reader has and nothing else. Two more are EARNED: the terminal, when
   somebody turns to the machine on the desk, and the board's own line,
   when the curtains come off it. The spec allows `registerTool` at any
   moment the document is active and the client hears a `toolchange`
   either way, so the ladder is not a trick: it is the API working as
   written. Tools persist across visits because the MARKS do — at load the
   struck set is read and whatever it has already earned is registered.

   THE AGENT IS NEVER TOLD MORE THAN A READER. The file card on the folded
   sheet carries six cryptic lines, all six written and each struck as it
   is found (FU1, 2026-09-27; copy.json `card.hint.*`;
   NC1 moved them there from the note under the map, which is the plan of
   glyphs again),
   and those same lines are what `survey_look` hands back and what an
   earned tool's description IS, word for word, with nothing added to
   explain them. The vocabulary is the hand's: cartouche, castoff, sound,
   fold, machine, bearing. Nothing here says "Easter egg", nothing says
   what a thing is for, and nothing counts anything down. An agent that
   solves this page solves it the way a reader does, by looking at it.

   WHAT A REFUSAL IS. One short physical line — "nothing there", "she has
   not cast off" — thrown, not returned, so an agent's own error path
   carries it. Every line is a PLACEHOLDER in copy.json (`webmcp.*`).

   FEATURE-DETECTED. Chrome's origin trial runs M149–156 and shipping is
   targeted at M157; with no `document.modelContext` this file registers
   nothing and costs one property read. `RoomWebMCP.execute(name, args)`
   runs any registered tool with or without a context, `RoomWebMCP.tools()`
   is the list as it stands, and `attach(ctx)` is the harness door.
   ===================================================================== */
(function () {
  'use strict';
  var root = window;
  var TERM_SRC = (window.assetUrl ? assetUrl('/js/room/terminal.js') : '/js/room/terminal.js');
  var termLoad = null;

  function R() { return root.Room || null; }
  function T(key, fields) {
    var C = root.RoomCopy;
    return C && C.t ? C.t(key, fields) : '[' + key + ']';
  }
  function no(key) { return new Error(T('webmcp.no.' + key)); }
  function n1(v) { return Math.round(v * 10) / 10; }

  /* terminal.js is fetched only when asked for (engine.js needTerm; the
     door in base.njk is gone, TD1); an agent's first call is the asking.
     Loaded, not shown. */
  function loadTerm() {
    if (root.RoomTerm) return Promise.resolve(root.RoomTerm);
    if (termLoad) return termLoad;
    termLoad = new Promise(function (res, rej) {
      /* terminal.js returns at once unless it was asked for (its own gate,
         the same word engine.js needTerm says) */
      root.ROOM_TERM_ASKED = 1;
      if (!document.querySelector('script[src="' + TERM_SRC + '"]')) {
        var t = document.createElement('script');
        t.src = TERM_SRC; t.defer = true;
        t.onerror = function () { rej(no('terminal')); };
        document.head.appendChild(t);
      }
      var t0 = Date.now();
      (function wait() {
        if (root.RoomTerm) return res(root.RoomTerm);
        if (Date.now() - t0 > 15000) return rej(no('terminal'));
        setTimeout(wait, 40);
      })();
    });
    return termLoad;
  }
  /* up, with the room turned to it, so the reader sees the line typed */
  function needTerm() {
    return loadTerm().then(function (Trm) {
      if (Trm.up && Trm.up()) return Trm;
      var r = R();
      if (r && r.terminal) return r.terminal(true).then(function () { return root.RoomTerm; });
      return Trm;
    });
  }
  function answer(o) { return JSON.stringify(o); }

  /* ====================================================================
     (1) THE EYES — the whole sheet at once

     A reader at the desk sees the whole window in one glance and the
     whole strip of years under it; an agent that can only probe one pixel
     at a time is not being given the same page. So `survey_look` draws
     the sheet TWICE: the window one character a day, and the record one
     character a week, both off the same field the shaders read
     (`Room.cellOf` for where a day is, `Room.dayAtMap` for whether the
     survey reaches it, `Room.bedAt` for what the ground is doing there).
     The ramp is relative to this sheet's own highest ground and deepest
     water, because a stranger's record has its own scale and a fixed one
     would read as flat paper.
     ==================================================================== */
  var SEA = '~', SHOAL = ':', FLAT = '-', TODAY = '+', BLANK = ' ';
  var LAND = [',', 'o', 'O', '#'];
  function legend() {
    var L = {};
    L[BLANK] = T('webmcp.legend.blank');
    L[SEA] = T('webmcp.legend.sea');
    L[SHOAL] = T('webmcp.legend.shoal');
    L[FLAT] = T('webmcp.legend.flat');
    L[LAND[0]] = T('webmcp.legend.low');
    L[LAND[1]] = T('webmcp.legend.ground');
    L[LAND[2]] = T('webmcp.legend.high');
    L[LAND[3]] = T('webmcp.legend.highest');
    L[TODAY] = T('webmcp.legend.today');
    return L;
  }
  /* every day the sheet draws, walked once: its date and where its cell
     is in map units. The record is a decade of days, which is a few
     thousand cheap sums — a tool call, never a frame. */
  function survey() {
    var r = R();
    if (!r || !r.years || !r.cellOf) return null;
    var yrs = r.years();
    if (!yrs.length) return null;
    var nd = (r.notableDates ? r.notableDates() : []) || [];
    var today = nd.length ? nd[nd.length - 1] : null;
    if (!today || !r.cellOf(today)) return null;
    var end = Date.parse(today + 'T00:00:00Z');
    var t = Date.UTC(yrs[0], 0, 1), days = [], c, iso;
    for (; t <= end; t += 86400000) {
      iso = new Date(t).toISOString().slice(0, 10);
      c = r.cellOf(iso);
      if (!c) continue;
      days.push({ d: iso, x: c[0], y: c[1] });
    }
    if (!days.length) return null;
    /* the seven rows and every week column, read off the cells themselves
       rather than worked out again from the layout's constants */
    var ys = [], xs = [], i, k;
    for (i = 0; i < days.length; i++) {
      k = Math.round(days[i].y * 10) / 10;
      if (ys.indexOf(k) < 0) ys.push(k);
      k = Math.round(days[i].x * 10) / 10;
      if (xs.indexOf(k) < 0) xs.push(k);
    }
    ys.sort(function (a, b) { return a - b; });
    xs.sort(function (a, b) { return a - b; });
    var rowOf = {}, colOf = {};
    for (i = 0; i < ys.length; i++) rowOf[ys[i]] = i;
    for (i = 0; i < xs.length; i++) colOf[xs[i]] = i;
    /* and what the ground is doing under each one */
    var hi = 0, lo = 0;
    for (i = 0; i < days.length; i++) {
      var D = days[i];
      D.row = rowOf[Math.round(D.y * 10) / 10];
      D.col = colOf[Math.round(D.x * 10) / 10];
      D.on = r.dayAtMap ? !!r.dayAtMap(D.x, D.y) : false;
      D.bed = r.bedAt ? +r.bedAt(D.x, D.y) : 0;
      if (D.bed > hi) hi = D.bed;
      if (D.bed < lo) lo = D.bed;
    }
    return { days: days, rows: ys.length, cols: xs.length, xs: xs,
             today: today, hi: hi, lo: lo };
  }
  /* A DAY'S CELL IS GROUND: the country is drawn out of the record and a
     day nobody worked is the flat, not the sea. Water is what the field
     gives back BELOW the coast, which out here is the basin west of the
     record, so both ends of the ramp are kept and each says what it is. */
  function charOf(s, D) {
    if (!D || !D.on) return BLANK;
    if (D.d === s.today) return TODAY;
    if (D.bed < 0) return (s.lo < 0 && D.bed > s.lo * 0.45) ? SHOAL : SEA;
    if (!(D.bed > 0) || !(s.hi > 0)) return FLAT;
    var q = D.bed / s.hi;
    return LAND[q > 0.62 ? 3 : q > 0.34 ? 2 : q > 0.12 ? 1 : 0];
  }
  var WINDOW_CAP = 120;                 /* columns; past this the strip is the answer */
  function grids(s) {
    var r = R(), out = {};
    /* THE WINDOW, one character a day. Which columns are under the glass
       is the view's own rectangle against the cells' own x. */
    var c = r.centre ? r.centre() : null;
    var weeks = r.windowWeeks ? r.windowWeeks() : 0;
    var cw = (c && weeks) ? c.w / weeks : 0;
    var lo = c ? c.x - c.w / 2 - cw / 2 : 0, hi = c ? c.x + c.w / 2 + cw / 2 : 0;
    var cols = [], i;
    for (i = 0; i < s.xs.length; i++) if (s.xs[i] >= lo && s.xs[i] <= hi) cols.push(i);
    var clipped = false;
    if (cols.length > WINDOW_CAP) {
      var mid = 0;
      for (i = 0; i < cols.length; i++) if (s.xs[cols[i]] <= (c ? c.x : 0)) mid = i;
      var from = Math.max(0, Math.min(cols.length - WINDOW_CAP, mid - (WINDOW_CAP >> 1)));
      cols = cols.slice(from, from + WINDOW_CAP);
      clipped = true;
    }
    var at = {}, j;
    for (i = 0; i < cols.length; i++) at[cols[i]] = i;
    var first = null, last = null, cells = [];
    for (j = 0; j < s.rows; j++) { cells.push([]); for (i = 0; i < cols.length; i++) cells[j].push(BLANK); }
    for (i = 0; i < s.days.length; i++) {
      var D = s.days[i];
      if (!(D.col in at)) continue;
      cells[D.row][at[D.col]] = charOf(s, D);
      if (D.on) { if (!first || D.d < first) first = D.d; if (!last || D.d > last) last = D.d; }
    }
    out.window = { weeks: cols.length, rows: s.rows, from: first, to: last,
                   clipped: clipped,
                   grid: cells.map(function (a) { return a.join(''); }) };
    /* THE WHOLE RECORD, one character a week: the highest ground the week
       reached, which is what the eye takes off a column at the fit. */
    var byCol = [], wFirst = [], wLast = [];
    for (i = 0; i < s.days.length; i++) {
      var E = s.days[i];
      if (!E.on) continue;
      if (!byCol[E.col] || E.bed > byCol[E.col].bed) byCol[E.col] = E;
      if (!wFirst[E.col] || E.d < wFirst[E.col]) wFirst[E.col] = E.d;
      if (!wLast[E.col] || E.d > wLast[E.col]) wLast[E.col] = E.d;
    }
    var strip = '';
    for (i = 0; i < s.cols; i++) strip += byCol[i] ? charOf(s, byCol[i]) : BLANK;
    out.strip = { weeks: s.cols, from: wFirst.length ? wFirst[0] : null,
                  to: s.today, grid: strip };
    out.legend = legend();
    return out;
  }

  /* ---- WHAT IS NAMED ON THE SHEET ------------------------------------
     In the sheet's own words, with map coordinates where a thing has any.
     Two of them are not there until the reader is: the machine is on a
     desk nobody is looking at until the room is up, and the window is
     behind a reader who is still sitting down. An agent gets the room a
     reader gets and not a floor plan of one. */
  function named() {
    var r = R(), o = {};
    var sub = r.subject ? r.subject() : null;
    var H = root.RoomHandle;
    o.cartouche = { subject: sub ? sub.handle : null, name: sub ? sub.name : null,
                    editing: !!(H && H.editing && H.editing()) };
    var V = r.voyage;
    if (V && V.state) {
      var vs = V.state();
      o.boat = { at: [n1(vs.x), n1(vs.y)], state: vs.state, course: Math.round(vs.hdg),
                 speed: vs.speed, depth: vs.depth, date: vs.date, wind: vs.wind ? n1(vs.wind.speed) : null };
      var ch = V.charted ? V.charted() : null;
      o.harbour = { charted: ch ? ch.n : null, of: ch ? ch.of : null,
                    destination: vs.destination,
                    marks: ch ? ch.marks.map(function (m) {
                      return { date: m.date, name: m.charted || m.seen ? m.name : null,
                               at: (m.charted || m.seen) ? [m.x, m.y] : null,
                               charted: m.charted, seen: m.seen };
                    }) : null };
      var g = V.guide ? V.guide() : null;
      if (g) o.lookout = { at: [n1(g.x), n1(g.y)], bearing: n1(g.bearing),
                           off: n1(g.delta), steady: !!g.aligned };
    }
    if (r.pit) {
      var d = r.pit();
      if (d && d.on) o.rough_ground = d;
    }
    o.corner = { fold: r.folded ? +r.folded().toFixed(3) : 0 };
    /* A CARD LYING ON THE DESK IS A THING IN THE ROOM. It is in front of
       a reader, so it is in the look — and while a discovery card is up
       she is hove to and the clock is held, which is a state an agent
       cannot be left to guess at. */
    var card = r.cardPresented ? r.cardPresented() : null;
    if (card) o.card = { date: card.date || null, title: card.title || null,
                         discovery: !!card.discovery };
    var up = !!(root.RoomTerm && root.RoomTerm.up && root.RoomTerm.up());
    if ((r.inRoom && r.inRoom()) || up) {
      o.machine = { turned: !!(r.lapUp && r.lapUp()), open: up,
                    screen: up ? (root.RoomTerm.browsing() ? 'browser' : 'terminal') : null };
    }
    if (r.inRoom && r.inRoom()) {
      var P = root.RoomPlace;
      o.window = { curtains: (P && P.curtains) ? P.curtains() : null };
    }
    return o;
  }

  /* ---- THE NOTE, AS IT STANDS ----------------------------------------
     NL1: the note under the folded map is a ruled list that shows the
     lines already done, struck through, and exactly ONE line after them.
     NC1 (2026-09-22): that list is the FILE CARD's now, on the folded
     sheet (the note is the plan of glyphs again); the field keeps its
     name, `note`, because a memorandum is a note wherever it lies.
     That is what is handed over — the same words, in the same order.
     FU1 (2026-09-27): the card writes every line from the start and
     strikes each as it is found (Tommy: "showing all hints at once and
     them crossing through as you find them"), so the agent is handed all
     six, each with whether it is struck. */
  function note() {
    var r = R(), G = r && r.progress;
    if (!G) return null;
    var list = G.list(), lines = [], open = 0, i, n, has;
    for (i = 0; i < list.length; i++) {
      n = list[i]; has = !!G.has(n);
      if (!has) open++;
      lines.push({ mark: n, line: T('card.hint.' + n), struck: has });
    }
    return { head: T('card.hint_head'), lines: lines,
             done: open ? null : T('card.hint_done') };
  }

  function view() {
    var r = R(), o = {};
    if (!r) return o;
    var c = r.centre ? r.centre() : null;
    if (c) o.centre = { x: n1(c.x), y: n1(c.y) };
    if (r.windowWeeks) o.weeks = +r.windowWeeks().toFixed(2);
    if (r.tiltDeg) o.tilt = n1(r.tiltDeg());
    if (r.yawDeg) o.yaw = n1(r.yawDeg());
    if (r.folded) o.fold = +r.folded().toFixed(3);
    if (r.inRoom) o.standing = !!r.inRoom();
    if (r.lapUp) o.laptop = !!r.lapUp();
    if (r.atFit) o.atFit = !!r.atFit();
    return o;
  }

  /* ====================================================================
     (2) THE TOOLS
     ==================================================================== */
  var ALL = {};                       /* name -> the tool, all of them */
  var ORDER = [];                     /* the order they were written in */
  var REG = {};                       /* name -> 1 once registered */
  var CTX = null;                     /* the real modelContext, if there is one */

  function tool(t) { ALL[t.name] = t; ORDER.push(t.name); return t; }

  tool({
    name: 'survey_look',
    description: T('webmcp.tool.look'),
    inputSchema: { type: 'object', properties: {} },
    annotations: { readOnlyHint: true },
    body: function () {
      var r = R();
      if (!r) throw no('sheet');
      var s = survey();
      var o = { ok: true, view: view(), things: named(), note: note() };
      if (s) {
        var g = grids(s);
        o.window = g.window; o.strip = g.strip; o.legend = g.legend;
        o.today = s.today;
      }
      var G = r.progress;
      if (G) o.marks = { struck: G.struck(), of: G.list().length };
      return o;
    }
  });

  tool({
    name: 'survey_read',
    description: T('webmcp.tool.read'),
    inputSchema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: T('webmcp.arg.date') },
        at: { type: 'array', items: { type: 'number' }, description: T('webmcp.arg.at') },
        px: { type: 'array', items: { type: 'number' }, description: T('webmcp.arg.px') }
      }
    },
    annotations: { readOnlyHint: true },
    body: function (a) {
      var r = R();
      if (!r) throw no('sheet');
      var o = { ok: true };
      if (a && a.px && a.px.length === 2) {
        var d = r.dayAt ? r.dayAt(+a.px[0], +a.px[1]) : null;
        var m = r.mapAt ? r.mapAt(+a.px[0], +a.px[1]) : null;
        if (!d && !m) throw no('thing');
        o.under = d ? { date: d.date, at: [n1(d.x), n1(d.y)] } : null;
        if (m) o.ground = { at: [n1(m.x), n1(m.y)], bed: r.bedAt ? n1(r.bedAt(m.x, m.y)) : null,
                            steps: m.steps };
        return o;
      }
      if (a && a.at && a.at.length === 2) {
        var x = +a.at[0], y = +a.at[1];
        var iso = r.dayAtMap ? r.dayAtMap(x, y) : null;
        o.under = iso ? { date: iso, at: [n1(x), n1(y)] } : null;
        o.ground = { at: [n1(x), n1(y)], bed: r.bedAt ? n1(r.bedAt(x, y)) : null };
        if (!iso && !o.ground.bed && o.ground.bed !== 0) throw no('thing');
        return o;
      }
      if (a && a.date) {
        var c = r.cellOf ? r.cellOf(String(a.date)) : null;
        if (!c) throw no('day');
        o.day = { date: String(a.date), at: [n1(c[0]), n1(c[1])],
                  surveyed: r.dayAtMap ? !!r.dayAtMap(c[0], c[1]) : null,
                  bed: r.bedAt ? n1(r.bedAt(c[0], c[1])) : null };
        return o;
      }
      /* nothing asked for: the index of the sheet, which is what a reader
         gets off the margin without touching anything */
      o.years = r.years ? r.years() : [];
      o.notable = r.notableDates ? r.notableDates() : [];
      if (r.dayStats) o.picks = r.dayStats();
      return o;
    }
  });

  tool({
    name: 'survey_move',
    description: T('webmcp.tool.move'),
    inputSchema: {
      type: 'object',
      properties: {
        how: { type: 'string', enum: ['pan', 'zoom', 'tilt', 'turn', 'goto', 'fit', 'home', 'out'] },
        x: { type: 'number' }, y: { type: 'number' },
        weeks: { type: 'number', description: T('webmcp.arg.weeks') },
        deg: { type: 'number', description: T('webmcp.arg.deg') },
        date: { type: 'string', description: T('webmcp.arg.date') }
      },
      required: ['how']
    },
    annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false },
    body: function (a) {
      var r = R();
      if (!r) throw no('sheet');
      var how = String((a && a.how) || '').toLowerCase();
      if (how === 'pan') {
        if (a.x === undefined || a.y === undefined) throw no('thing');
        r.centreOn(+a.x, +a.y, a.weeks ? +a.weeks * (r.centre().w / r.windowWeeks()) : 0);
      } else if (how === 'zoom') {
        if (!(+a.weeks > 0)) throw no('weeks');
        r.zoomWeeks(+a.weeks);
      } else if (how === 'tilt') {
        if (a.deg === undefined) throw no('deg');
        r.attitude(+a.deg, null);
      } else if (how === 'turn') {
        if (a.deg === undefined) throw no('deg');
        r.attitude(null, +a.deg);
      } else if (how === 'goto') {
        if (!a.date) throw no('day');
        if (!r.goto(String(a.date))) throw no('day');
      } else if (how === 'fit') {
        r.fit();
      } else if (how === 'home') {
        r.home(true);
      } else if (how === 'out') {
        /* THE WHEEL OUT. It is the one move that is also an act — winding
           out over a folded sheet is how a reader stands up — so it goes
           through the engine's own `wheelOut`, the same function the
           wheel handler calls, and earns what that earns. */
        if (!r.wheelOut) throw no('sheet');
        if (!r.wheelOut()) throw no('fold');
      } else {
        throw no('word');
      }
      return { ok: true, view: view() };
    }
  });

  tool({
    name: 'survey_act',
    description: T('webmcp.tool.act'),
    inputSchema: {
      type: 'object',
      properties: {
        thing: { type: 'string',
                 enum: ['cartouche', 'castoff', 'steer', 'throttle', 'sound', 'card',
                        'fold', 'unfold', 'machine', 'step', 'bearing'] },
        value: { type: 'string', description: T('webmcp.arg.value') }
      },
      required: ['thing']
    },
    annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false },
    body: function (a) {
      var r = R();
      if (!r) throw no('sheet');
      var what = String((a && a.thing) || '').toLowerCase();
      var v = (a && a.value !== undefined && a.value !== null) ? String(a.value).trim() : '';
      var out = { ok: true, thing: what };

      if (what === 'cartouche') {
        /* THE ENTER KEY OF THE NAME PANEL, and nothing else: handle.js's
           own `enter`, which is the one function a reader's Enter goes
           through too — so the guards are the same guards. An empty value
           is the box rubbed out, which is how a record is put back. */
        var H = root.RoomHandle;
        if (!H || !H.enter) throw no('cartouche');
        var pr = H.enter(v);
        if (!pr || !pr.then) {
          return { ok: true, thing: what, changed: false,
                   subject: (r.subject && r.subject()) ? r.subject().handle : null };
        }
        return pr.then(function (j) {
          return { ok: true, thing: what, changed: true,
                   subject: (r.subject && r.subject()) ? r.subject().handle : null,
                   name: (j && (j.name || j.handle)) || null, view: view() };
        }, function (e) { throw new Error(String((e && e.message) || e)); });
      }

      if (what === 'castoff' || what === 'steer' || what === 'throttle' || what === 'sound') {
        var V = r.voyage;
        if (!V) throw no('water');
        if (what === 'castoff') out.said = V.state().state === 'arrived' ? V.recast() : V.sail();
        else if (what === 'steer') {
          if (v === '' || isNaN(+v)) throw no('deg');
          out.said = V.steer(+v);
        } else if (what === 'throttle') {
          /* ==== THE TWO ARROW KEYS, UNDER A WORD, and the table cannot do
             without them. A mark charted heaves her to (voyage.js
             `heaveTo`); when this was written she was handed back at HALF
             the order she was under, so seven marks were seven halvings
             and a door with no throttle could never finish the passage.
             HV2 (2026-09-23) hands her back under the same order, but a
             hand still has ArrowUp and a door with no throttle is still a
             door that cannot change her pace; this is ArrowUp. It is the
             terminal's own word and the same three values. Found by
             walking the six with the tools alone, 2026-09-22. */
          if (!V.throttle || !V.setSpeed) throw no('water');
          var w = v.toLowerCase();
          if (w === 'stop') out.said = V.setSpeed(0);
          else if (w === 'up' || w === '') out.said = V.throttle(1);
          else if (w === 'down') out.said = V.throttle(-1);
          else throw no('throttle');
          out.said = { knots: +(+out.said).toFixed(2) };
        } else out.said = V.sound();
        var nm = named();
        out.boat = nm.boat; out.harbour = nm.harbour; out.lookout = nm.lookout;
        return out;
      }

      if (what === 'card') {
        /* THE CARD IS PUT DOWN, and it is a HAND that puts it down. Only
           a hand's reason (`button`, the card's own; `escape`) hands the
           boat back — day.js: "Only a hand putting the card down may
           restart a voyage" — and a tool that could not do that could
           chart a mark and then be stopped dead beside it for ever, which
           is the one thing this table may not be. This is the button,
           because pressing the button is what the tool is doing. Found by
           walking the six with the tools alone, 2026-09-22. (MO1: the
           terminal's `sheet card off` hands her back too, since the link
           has no other way to put a card down — day.js takes `terminal`.) */
        if (!r.cardDismiss) throw no('sheet');
        if (!r.cardDismiss('button')) throw no('card');
        out.card = null;
        return out;
      }

      if (what === 'fold') {
        /* the terminal's own `sheet fold`, to the letter: no number is the whole
           fold, the way letting go of the corner past the snap is, and a
           number is the hand held still at it */
        if (!r.foldTo || !r.foldAt) throw no('sheet');
        if (v === '') r.foldTo(1);
        else {
          var to = +v;
          if (isNaN(to) || to < 0 || to > 1) throw no('amount');
          r.foldAt(to);
        }
        out.fold = r.folded ? +r.folded().toFixed(3) : 0;
        return out;
      }
      if (what === 'unfold') { r.unfold(); out.fold = r.folded ? +r.folded().toFixed(3) : 0; return out; }

      if (what === 'machine') {
        if (!r.terminal) throw no('machine');
        /* THE RUNG. Turning to the machine is what puts the terminal in
           the table — asked for here, before the turn has even finished,
           so an agent watching `toolchange` hears it on this call. */
        earn('survey_terminal');
        return r.terminal(true).then(function () {
          return { ok: true, thing: what, machine: named().machine, tools: tools() };
        }, function (e) { throw new Error(String((e && e.message) || e)); });
      }

      if (what === 'step') {
        if (!r.doomStep) throw no('ground');
        if (!v) throw no('day');
        if (!r.doomStep(v)) throw no('step');
        out.rough_ground = r.pit();
        return out;
      }
      if (what === 'bearing') {
        /* AL1: a station and a bearing — `north 1.52` — which is what a
           hand turning that station's limb and letting go of it does (AL2:
           the limb is the only handle a hand has on a line now):
           the instrument walks there and the line is laid. Degrees, y
           down, so a positive bearing swings south. The bearing that would
           bear on the pit is not handed over: rough_ground has the two
           stations' map points and the pit's centre, and the trig is the
           agent's, as the eye is the reader's. */
        if (!r.doomBearing) throw no('ground');
        var bw = v.toLowerCase().split(/[\s,]+/).filter(Boolean);
        if (bw.length !== 2 || isNaN(+bw[1]) || !/^(north|south|n|s)$/.test(bw[0])) throw no('bearing');
        /* a well-said bearing the ground will not take is a ground with
           no instrument up on it: act one, or the pit already stood up */
        if (!r.doomBearing(bw[0], +bw[1])) throw no('ground');
        out.rough_ground = r.pit();
        return out;
      }
      throw no('word');
    }
  });

  /* ---- THE STATE, unchanged since WM1 -------------------------------- */
  tool({
    name: 'survey_state',
    description: T('webmcp.tool.state'),
    inputSchema: { type: 'object', properties: {} },
    annotations: { readOnlyHint: true },
    body: function () {
      return loadTerm().then(function (Trm) { return { ok: true, state: Trm.state() }; });
    }
  });

  /* ---- EARNED: the machine's own screen ------------------------------
     The description is the file card's hint line, word for word, and nothing is
     added to it. What the tool TAKES is in the schema, where a reader
     would find it printed on the prompt. */
  tool({
    name: 'survey_terminal',
    description: T('card.hint.laptop'),
    inputSchema: {
      type: 'object',
      properties: { command: { type: 'string', description: T('webmcp.arg.command') } },
      required: ['command']
    },
    annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false },
    body: function (a) {
      var cmd = String(a && a.command != null ? a.command : '').replace(/[\r\n][\s\S]*$/, '');
      if (!cmd.trim()) throw no('command');
      return needTerm().then(function (Trm) { return Trm.remote(cmd); })
        .then(function (o) { return { ok: true, output: o, state: root.RoomTerm.state() }; },
              function (e) { throw new Error(String((e && e.message) || e)); });
    }
  });

  /* MO1 (2026-09-23): THERE IS NO BOARD TOOL HERE. WM2 put
     `survey_board_enter` on this ladder, writing through the terminal's
     `board name`. Tommy: "The whole point with Web MCP is that it can use
     the page for you, but it would still need to use a proper agent
     session to link via the remote MCP ... the whole point of the last
     Easter egg is you have to connect to remote MCP to complete the game."
     So the card is the remote server's tool alone (api/_lib/mcp.js
     `survey_board_enter`, with a session from `survey_link`), and what
     this file tells an agent is where that door is: `link` on the machine
     on the desk, and `link info` for the endpoint. */

  /* ====================================================================
     (3) THE LADDER

     `registerTool` may be called at any time the document is active
     (spec, 17 Sept 2026 draft) and answers a promise that REJECTS on a
     name already taken; tools go by an AbortSignal in the options, and
     the agent hears a `toolchange` event either way. So a rung put up
     twice is not an error here, only a warning, and `earn` is idempotent.
     ==================================================================== */
  function put(name) {
    if (!CTX || !ALL[name]) return false;
    try {
      var r = CTX.registerTool(ALL[name]);
      if (r && typeof r.then === 'function') {
        r.then(null, function (e) { try { console.warn('webmcp', name, e); } catch (e2) {} });
      }
      return true;
    } catch (e) { try { console.warn('webmcp', name, e); } catch (e2) {} return false; }
  }
  function earn(name) {
    if (REG[name] || !ALL[name]) return false;
    REG[name] = 1;
    put(name);
    return true;
  }
  function tools() {
    var out = [], i;
    for (i = 0; i < ORDER.length; i++) if (REG[ORDER[i]]) out.push(ORDER[i]);
    return out;
  }
  /* THE FOUR THAT STAND FROM THE FIRST FRAME, and the state beside them */
  var DAY_ONE = ['survey_look', 'survey_read', 'survey_move', 'survey_act', 'survey_state'];

  /* WHAT THE STRUCK SET ALREADY EARNS. Marks persist, so tools do: a
     reader who found the machine last week comes back to a page that has
     the terminal in its table before the first call. The machine is also
     earned by a HAND — a press on the laptop — which no published
     function reports, so the check rides on the reader's own events and
     takes itself off the document once there is nothing left to earn. */
  function sync() {
    var r = R();
    var up = !!(root.RoomTerm && root.RoomTerm.up && root.RoomTerm.up());
    if (up || (r && r.lapUp && r.lapUp())) earn('survey_terminal');
    if (REG.survey_terminal) drop();          /* MO1: the machine is the last rung */
    return tools();
  }
  var watching = 0;
  function watch() {
    if (watching) return;
    watching = 1;
    try {
      document.addEventListener('pointerup', sync, { capture: true, passive: true });
      document.addEventListener('keyup', sync, { capture: true, passive: true });
    } catch (e) { watching = 0; }
  }
  function drop() {
    if (!watching) return;
    watching = 0;
    try {
      document.removeEventListener('pointerup', sync, true);
      document.removeEventListener('keyup', sync, true);
    } catch (e) { /* a document that takes no listeners never had these */ }
  }

  /* ---- RUNNING ONE -----------------------------------------------------
     THE DOOR IS SET FIRST, ALWAYS. progress.js keeps a sticky last actor
     and a call count (`Room.progress.call`) and the journal reads both, so
     every execute — refused or not — says a tool did this. A refusal is an
     Error carrying one physical line; anything else is JSON. */
  function run(name, input) {
    var t = ALL[name];
    /* AND A RUNG THAT HAS NOT BEEN EARNED IS NOT THERE. `execute` is the
       harness's door as much as the context's, and a ladder a harness can
       step over is not a ladder. */
    if (!t || !REG[name]) return Promise.reject(no('word'));
    try {
      var r = R();
      if (r && r.progress && r.progress.call) r.progress.call('tool');
    } catch (e) { /* a room with no journal is still a room */ }
    sync();
    return new Promise(function (res) { res(t.body(input || {})); })
      .then(function (o) { return answer(o); });
  }
  /* every tool's `execute` is the same one line, so nothing can be in one
     of them and not the others */
  (function () {
    for (var i = 0; i < ORDER.length; i++) {
      (function (name) {
        ALL[name].execute = function (input) { return run(name, input); };
      })(ORDER[i]);
    }
  })();

  function attach(ctx) {
    if (!ctx || typeof ctx.registerTool !== 'function') return 0;
    CTX = ctx;
    var n = 0, list = tools(), i;
    for (i = 0; i < list.length; i++) if (put(list[i])) n++;
    return n;
  }
  function context() {
    try { return document.modelContext || null; } catch (e) { return null; }
  }

  root.RoomWebMCP = {
    attach: attach,
    tools: tools,
    table: ALL,
    execute: function (name, args) { return run(String(name), args); },
    earn: earn,
    sync: sync,
    loadTerm: loadTerm,
    registered: 0
  };

  /* the plain page has no room to drive, and PH5: the phone's desk has
     no room, terminal or marks for an agent to drive either */
  if (!/\b(nogl|phone)\b/.test(document.documentElement.className)) {
    for (var i = 0; i < DAY_ONE.length; i++) REG[DAY_ONE[i]] = 1;
    /* and whatever the struck set already earns, before the first call */
    sync();
    watch();
    try {
      var r0 = R();
      if (r0 && r0.progress && r0.progress.onChange) r0.progress.onChange(sync);
    } catch (e) { /* the marks will still be read on every call */ }
    var ctx0 = context();
    if (ctx0) root.RoomWebMCP.registered = attach(ctx0);
  }
})();
